import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, LogOut, Bell, X } from "lucide-react";
import api from "@/lib/api";

/* Notification bell — unread city mail (bulletins, invites, alerts) on every hub page. */
function HubBell() {
  const [mail, setMail] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => {
    if (!localStorage.getItem("nc_token")) return;
    api.get("/nc/community/inbox").then((r) => setMail(r.data)).catch(() => {});
  };
  useEffect(load, []);
  if (!mail) return null;
  const read = (m) => {
    if (m.status === "unread") api.post(`/nc/community/inbox/${m.id}/read`).then(load).catch(() => {});
  };
  return (
    <>
      <button data-testid="hub-bell" onClick={() => setOpen((o) => !o)}
        className="relative flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400 hover:text-nc-blue px-2.5 py-1.5 transition-colors duration-300">
        <Bell className="w-3.5 h-3.5" />
        {mail.unread > 0 && <span data-testid="hub-bell-badge" className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full text-[8px] font-bold flex items-center justify-center text-black bg-nc-orange">{mail.unread}</span>}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[55]" onClick={() => setOpen(false)} />
          <div data-testid="hub-bell-panel" className="fixed top-12 right-2 z-[60] w-[340px] max-h-[420px] overflow-y-auto bg-[#04060a] border border-white/15 shadow-[0_0_60px_rgba(0,0,0,0.8)]" style={{ scrollbarWidth: "thin" }}>
            <div className="sticky top-0 flex items-center justify-between px-4 py-3 border-b border-white/10 bg-[#04060a]">
              <p className="font-mono text-[9px] font-bold uppercase tracking-[0.3em] text-zinc-200">City mail</p>
              <button data-testid="hub-bell-close" onClick={() => setOpen(false)} className="text-zinc-500 hover:text-zinc-200"><X className="w-3.5 h-3.5" /></button>
            </div>
            {(mail.mail || []).length === 0 && <p className="font-mono text-[10px] text-zinc-500 px-4 py-6">No mail yet — the city will write to you here.</p>}
            {(mail.mail || []).map((m) => (
              <button key={m.id} data-testid="hub-mail-row" onClick={() => read(m)}
                className={`w-full text-left px-4 py-3 border-b border-white/[0.06] hover:bg-white/[0.03] transition-colors ${m.status === "unread" ? "bg-nc-blue/[0.04]" : ""}`}>
                <p className="flex items-center gap-2">
                  {m.status === "unread" && <span className="w-1.5 h-1.5 rounded-full bg-nc-orange shrink-0" />}
                  <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-500">{m.kind}</span>
                  <span className="ml-auto font-mono text-[8px] text-zinc-600">{new Date(m.created_at).toLocaleDateString()}</span>
                </p>
                <p className={`font-mono text-[10px] mt-1 ${m.status === "unread" ? "text-zinc-100 font-bold" : "text-zinc-300"}`}>{m.title}</p>
                {m.body && <p className="font-mono text-[9px] text-zinc-500 mt-0.5 leading-relaxed line-clamp-2">{m.body}</p>}
              </button>
            ))}
          </div>
        </>
      )}
    </>
  );
}

export default function PortalNav({ title, accent = "text-nc-blue" }) {
  const navigate = useNavigate();
  const goBack = () => {
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate("/");
  };
  const logout = () => {
    localStorage.removeItem("nc_token");
    navigate("/auth");
  };
  const linkCls = "font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400 hover:text-nc-blue px-2.5 py-1.5 transition-colors duration-300";

  return (
    <div data-testid="portal-nav" className="flex items-center justify-between gap-3 border border-white/10 bg-black/40 px-3 py-2">
      <div className="flex items-center gap-2 min-w-0">
        <button
          data-testid="portal-back-btn"
          onClick={goBack}
          className="flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-300 hover:text-nc-blue border border-white/15 hover:border-nc-blue/50 px-2.5 py-1.5 transition-colors duration-300"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <Link to="/" data-testid="portal-nav-brand" className="flex items-center gap-2 pl-1">
          <img src="/assets/specpixel-logo-transparent.png" alt="Specpixel Entertainment" className="brand-mark h-6 w-6 rounded-full" />
          <span className="hidden sm:block font-mono text-[9px] tracking-[0.3em] text-zinc-500 uppercase">Specpixel</span>
        </Link>
        {title && <span className={`hidden md:block font-mono text-[9px] tracking-[0.25em] uppercase ${accent}`}>{"// "}{title}</span>}
      </div>
      <div className="flex items-center">
        <HubBell />
        <Link to="/" data-testid="portal-nav-home" className={linkCls}>Main Site</Link>
        <Link to="/profile" data-testid="portal-nav-profile" className={linkCls}>Profile</Link>
        <Link to="/support" data-testid="portal-nav-support" className={linkCls}>Support</Link>
        <button
          data-testid="portal-nav-logout"
          onClick={logout}
          className="flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400 hover:text-red-400 px-2.5 py-1.5 transition-colors duration-300"
        >
          <LogOut className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Log out</span>
        </button>
      </div>
    </div>
  );
}
